import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloud, FileText, CheckCircle, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { StepIndicator } from '../components/StepIndicator';
import { DistributionPreview } from '../components/DistributionPreview';
import { MOCK_TEAMS } from '../data/mockData';
import { Team } from '../types';
export function LeadUploadPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const steps = [{
    id: 1,
    label: 'Upload CSV'
  }, {
    id: 2,
    label: 'Preview Data'
  }, {
    id: 3,
    label: 'Distribution'
  }, {
    id: 4,
    label: 'Confirm'
  }];
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      setFile(droppedFile);
    }
  };
  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(c => c + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(c => c - 1);
  };
  const toggleTeam = (teamId: string) => {
    setSelectedTeams(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);
  };
  const handleUpload = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setCurrentStep(5); // Success state
    }, 2000);
  };
  // Render Step Content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-indigo-500 transition-colors cursor-pointer bg-gray-50" onDragOver={e => e.preventDefault()} onDrop={handleFileDrop} onClick={() => document.getElementById('file-upload')?.click()} data-id="element-186">
            <input id="file-upload" type="file" accept=".csv" className="hidden" onChange={e => e.target.files && setFile(e.target.files[0])} data-id="element-187" />
            <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-188">
              <UploadCloud className="w-8 h-8" data-id="element-189" />
            </div>
            <h3 className="text-lg font-medium text-gray-900" data-id="element-190">
              {file ? file.name : 'Drop your CSV file here'}
            </h3>
            <p className="text-gray-500 mt-2 text-sm" data-id="element-191">
              {file ? `${(file.size / 1024).toFixed(2)} KB` : 'or click to browse from your computer'}
            </p>
          </div>;
      case 2:
        return <div className="space-y-4" data-id="element-192">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-193">
              <div className="flex" data-id="element-194">
                <div className="flex-shrink-0" data-id="element-195">
                  <AlertCircle className="h-5 w-5 text-blue-400" data-id="element-196" />
                </div>
                <div className="ml-3" data-id="element-197">
                  <p className="text-sm text-blue-700" data-id="element-198">
                    Found 245 borrower leads in{' '}
                    <strong data-id="element-199">{file?.name || 'uploaded file'}</strong>. Showing
                    first 5 rows preview.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-200">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-201">
                <thead className="bg-gray-50" data-id="element-202">
                  <tr data-id="element-203">
                    {['First Name', 'Last Name', 'Email', 'Phone', 'Loan Purpose', 'State'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-204">
                        {h}
                      </th>)}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-205">
                  {[{
                  first: 'Maria',
                  last: 'Gonzalez',
                  email: 'maria.g@gmail.com',
                  phone: '555-0142',
                  purpose: 'Purchase',
                  state: 'CA'
                }, {
                  first: 'James',
                  last: 'Chen',
                  email: 'jchen@outlook.com',
                  phone: '555-0287',
                  purpose: 'Refinance',
                  state: 'TX'
                }, {
                  first: 'Ashley',
                  last: 'Williams',
                  email: 'awilliams@yahoo.com',
                  phone: '555-0391',
                  purpose: 'FHA Purchase',
                  state: 'FL'
                }, {
                  first: 'Robert',
                  last: 'Kim',
                  email: 'r.kim@gmail.com',
                  phone: '555-0455',
                  purpose: 'VA Purchase',
                  state: 'AZ'
                }, {
                  first: 'Stephanie',
                  last: 'Patel',
                  email: 's.patel@hotmail.com',
                  phone: '555-0518',
                  purpose: 'Cash-Out Refi',
                  state: 'CO'
                }].map((lead, i) => <tr key={i} data-id="element-206">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-207">
                        {lead.first}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-208">
                        {lead.last}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-209">
                        {lead.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-210">
                        {lead.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-211">
                        {lead.purpose}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-212">
                        {lead.state}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-213">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-214">
              Select Teams to Distribute Leads
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-215">
              {MOCK_TEAMS.map(team => <div key={team.id} onClick={() => toggleTeam(team.id)} className={`
                    cursor-pointer border rounded-lg p-4 flex items-center justify-between transition-all
                    ${selectedTeams.includes(team.id) ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-gray-200 hover:border-gray-300'}
                  `} data-id="element-216">
                  <div data-id="element-217">
                    <p className="font-medium text-gray-900" data-id="element-218">{team.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-219">
                      {team.users.length} users • {team.distributionMethod}
                    </p>
                  </div>
                  {selectedTeams.includes(team.id) && <CheckCircle className="w-5 h-5 text-indigo-600" data-id="element-220" />}
                </div>)}
            </div>

            {selectedTeams.length > 0 && <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-221">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-222">
                  Distribution Preview
                </h3>
                <div className="bg-gray-50 p-6 rounded-lg space-y-8" data-id="element-223">
                  {selectedTeams.map(teamId => {
                const team = MOCK_TEAMS.find(t => t.id === teamId)!;
                const leadsPerTeam = Math.floor(245 / selectedTeams.length); // Mock 245 leads
                return <div key={team.id} data-id="element-224">
                        <p className="font-medium text-gray-900 mb-2" data-id="element-225">
                          {team.name}{' '}
                          <span className="text-gray-500 font-normal" data-id="element-226">
                            ({leadsPerTeam} leads)
                          </span>
                        </p>
                        <DistributionPreview users={team.users} distributionMethod={team.distributionMethod} totalLeads={leadsPerTeam} data-id="element-227" />
                      </div>;
              })}
                </div>
              </div>}
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-228">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-229">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-230">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-231">Summary</h3>
              </div>
              <div className="p-6 space-y-4" data-id="element-232">
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-233">
                  <span className="text-gray-500" data-id="element-234">File Name</span>
                  <span className="font-medium text-gray-900" data-id="element-235">
                    {file?.name}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-236">
                  <span className="text-gray-500" data-id="element-237">Total Leads</span>
                  <span className="font-medium text-gray-900" data-id="element-238">245</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-239">
                  <span className="text-gray-500" data-id="element-240">Selected Teams</span>
                  <span className="font-medium text-gray-900" data-id="element-241">
                    {selectedTeams.length}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-242">
                  <span className="text-gray-500" data-id="element-243">Est. Processing Time</span>
                  <span className="font-medium text-gray-900" data-id="element-244">~2 seconds</span>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4" data-id="element-245">
              <div className="flex" data-id="element-246">
                <div className="flex-shrink-0" data-id="element-247">
                  <AlertCircle className="h-5 w-5 text-yellow-400" data-id="element-248" />
                </div>
                <div className="ml-3" data-id="element-249">
                  <p className="text-sm text-yellow-700" data-id="element-250">
                    This action will immediately distribute leads to Bonzo.
                    Ensure all team configurations are correct before
                    proceeding.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 5:
        return <div className="text-center py-12" data-id="element-251">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-252">
              <CheckCircle className="w-10 h-10" data-id="element-253" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-254">
              Distribution Complete!
            </h2>
            <p className="text-gray-500 mb-8" data-id="element-255">
              245 leads have been successfully distributed to{' '}
              {selectedTeams.length} teams.
            </p>
            <Button onClick={() => {
            setFile(null);
            setCurrentStep(1);
            setSelectedTeams([]);
          }} data-id="element-256">
              Upload Another Batch
            </Button>
          </div>;
    }
  };
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-257">
      <div className="mb-8" data-id="element-258">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-259">Upload Leads</h1>
        <p className="text-gray-500 mt-1" data-id="element-260">
          Import mortgage leads from Salesforce and distribute to loan officer
          teams
        </p>
      </div>

      <Card className="min-h-[600px] flex flex-col" data-id="element-261">
        {currentStep < 5 && <StepIndicator steps={steps} currentStep={currentStep} data-id="element-262" />}

        <div className="flex-1 py-6 px-4 md:px-12" data-id="element-263">
          <AnimatePresence mode="wait" data-id="element-264">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-265">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {currentStep < 5 && <div className="border-t border-gray-200 p-6 flex justify-between bg-gray-50 rounded-b-lg" data-id="element-266">
            <Button variant="secondary" onClick={handleBack} disabled={currentStep === 1} leftIcon={<ArrowLeft className="w-4 h-4" data-id="element-268" />} data-id="element-267">
              Back
            </Button>

            {currentStep === 4 ? <Button onClick={handleUpload} isLoading={isProcessing} leftIcon={<UploadCloud className="w-4 h-4" data-id="element-270" />} data-id="element-269">
                Confirm & Distribute
              </Button> : <Button onClick={handleNext} disabled={!file || currentStep === 3 && selectedTeams.length === 0} rightIcon={<ArrowRight className="w-4 h-4" data-id="element-272" />} data-id="element-271">
                Next Step
              </Button>}
          </div>}
      </Card>
    </div>;
}